import { ErrorDeAcceso } from '../identidad/errores';
import type {
  Administracion,
  AltaDeInversionista,
  DatosDeInvitacion,
  PuertoDeDatos,
} from './puerto';
import type { CifrasDelPanel } from './tipos';

/**
 * El de desarrollo: **no inventa ni una cifra**.
 *
 * Es el heredero directo de `datos/cifrasPublicas.ts`, el archivo que el panel
 * leía hasta el 2026-09-21, y conserva lo único que de verdad importaba de él:
 * la regla de que **si una cifra no se puede proyectar en una pantalla en una
 * sala llena de desconocidos, no entra aquí**. Un adaptador de desarrollo con
 * datos bonitos inventados es la forma más rápida de que un número falso acabe
 * en una captura de pantalla que alguien enseña.
 *
 * Así que devuelve el estado vacío honesto:
 *
 * - `cobertura: null` — hay una fuente real, con 12 de 32 estados, pero vive en
 *   el servidor y detrás del token. Aquí no.
 * - `embudo.hayDatos: false` — las tablas que lo alimentan están vacías, y
 *   seis ceros afirmarían que se midió y no hubo nada.
 *
 * El corte sí va: es una fecha, no una medición. Dice **cuándo** se miró, no
 * **qué** se encontró.
 *
 * ## Lo de administración falla, y es lo correcto
 *
 * Sin servidor no hay cuentas que listar. Devolver una lista inventada haría
 * que la pantalla se viera bien en local y se cayera en el primer entorno de
 * verdad; devolver una vacía diría que no hay nadie dado de alta, que es una
 * afirmación falsa. Lanza `sinConfigurar`, que la pantalla ya sabe traducir.
 */
export class AdaptadorDatosLocal implements PuertoDeDatos {
  readonly esDeMentira = true;

  async cifras(): Promise<CifrasDelPanel> {
    return {
      corte: '2026-08-21',
      cobertura: null,
      embudo: {
        resumen: {
          solicitudesDeContacto: 0,
          cuentasCreadas: 0,
          pidieronDemo: 0,
          enEspera: 0,
          demosAtendidas: 0,
          conversionADemoPorcentaje: 0,
          hayDatos: false,
        },
        porIndustria: {},
        porTamanoEmpresa: {},
        porTipoDeSolicitud: {},
        porPerfil: {},
        altasPorMes: [],
        solicitudesPorMes: [],
      },
    };
  }

  async inversionistas(): Promise<Administracion> {
    throw new ErrorDeAcceso('sinConfigurar');
  }

  async invitar(_datos: DatosDeInvitacion): Promise<AltaDeInversionista> {
    throw new ErrorDeAcceso('sinConfigurar');
  }
}
